import { useEffect, useRef, useState } from "react";
import { Bell, CalendarPlus, HelpCircle, ListChecks, Plus, Search, Settings, X } from "lucide-react";
import type { EventKind } from "../api/types";
import { useFinanceMonth, useHealthDemands, useResolutions, useTasks } from "../api/hooks";
import { overdueExpenses } from "../lib/finance";
import { newDraft } from "../lib/draft";
import { anchorOf, useUi, type Anchor, type Page } from "../state/ui";
import { Menu } from "./ui/Menu";
import { Logo } from "./Logo";
import "./AppNav.css";

export const SECTIONS: Array<{ page: Page; label: string }> = [
  { page: "home", label: "Início" },
  { page: "calendar", label: "Agenda" },
  { page: "tasks", label: "Tarefas" },
  { page: "resolutions", label: "Resoluções" },
  { page: "health", label: "Saúde" },
  { page: "finance", label: "Finanças" },
  { page: "cycle", label: "Ciclo menstrual" }
];

/** Pending counts shown next to the sections that have them. */
export function useSectionCounts(): Partial<Record<Page, number>> {
  const { today } = useUi();
  const month = useFinanceMonth(today.slice(0, 7)).data;
  return {
    tasks: useTasks().data?.pending.length ?? 0,
    resolutions: useResolutions().data?.pending.length ?? 0,
    health: useHealthDemands().data?.pending.length ?? 0,
    finance: month ? overdueExpenses(month.entries, today).length : 0
  };
}

export function isSectionActive(current: Page, page: Page): boolean {
  return current === page || (page === "calendar" && current === "search");
}

/** Global nav: wordmark left, sections centered, search / new / account right. */
export function AppNav({ onAccount }: { onAccount: (a: Anchor) => void }) {
  const ui = useUi();
  const counts = useSectionCounts();
  const [searching, setSearching] = useState(ui.page === "search");
  const [query, setQuery] = useState(ui.searchQuery);
  const [newMenu, setNewMenu] = useState<Anchor | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searching) searchRef.current?.focus();
  }, [searching]);

  // "/" focuses the search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        setSearching(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const closeSearch = () => {
    setSearching(false);
    setQuery("");
    if (ui.page === "search") ui.setPage("calendar");
  };

  const startCreate = (kind: EventKind) => {
    const date = ui.page === "calendar" ? ui.date : ui.today;
    ui.setOverlay({ kind: "editor", draft: newDraft({ date, startMin: 9 * 60, settings: ui.me.settings, calendars: ui.calendars, kind }) });
  };

  return (
    <header className="nav">
      <button className="nav-brand" onClick={() => ui.setPage("home")} aria-label="Início">
        <Logo />
      </button>

      <nav className="nav-links" aria-label="Seções">
        {SECTIONS.map(({ page, label }) => {
          const active = isSectionActive(ui.page, page);
          return (
            <button key={page} className={`nav-link${active ? " active" : ""}`} aria-current={active ? "page" : undefined} onClick={() => ui.setPage(page)}>
              {label}
              {(counts[page] ?? 0) > 0 && <span className="nav-count">{counts[page]}</span>}
            </button>
          );
        })}
      </nav>

      <div className="nav-actions">
        {searching ? (
          <form
            className="nav-search"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              ui.search(query);
            }}
          >
            <Search size={16} />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar na agenda"
              aria-label="Buscar eventos"
              onKeyDown={(e) => e.key === "Escape" && closeSearch()}
            />
            <button type="button" className="icon-btn sm" aria-label="Fechar busca" onClick={closeSearch}>
              <X size={16} />
            </button>
          </form>
        ) : (
          <button className="icon-btn" aria-label="Buscar" title="Buscar (/)" onClick={() => setSearching(true)}>
            <Search size={18} />
          </button>
        )}
        <button className="icon-btn hide-narrow" aria-label="Atalhos de teclado" title="Atalhos (?)" onClick={() => ui.setOverlay({ kind: "shortcuts" })}>
          <HelpCircle size={18} />
        </button>
        <button className={`icon-btn hide-narrow${ui.page === "settings" ? " on" : ""}`} aria-label="Configurações" onClick={() => ui.setPage("settings")}>
          <Settings size={18} />
        </button>
        <button className="btn primary nav-new" onClick={(e) => setNewMenu(anchorOf(e.currentTarget))} aria-haspopup="menu">
          <Plus size={16} strokeWidth={2.4} />
          <span>Novo</span>
        </button>
        <button className="nav-avatar" aria-label="Conta" onClick={(e) => onAccount(anchorOf(e.currentTarget))}>
          {(ui.me.name ?? "M").trim().charAt(0).toUpperCase()}
        </button>
      </div>

      {newMenu && (
        <Menu
          anchor={newMenu}
          onClose={() => setNewMenu(null)}
          items={[
            { key: "event", label: "Evento", icon: <CalendarPlus size={16} />, shortcut: "C", onSelect: () => startCreate("event") },
            { key: "task", label: "Tarefa", icon: <ListChecks size={16} />, onSelect: () => startCreate("task") },
            { key: "reminder", label: "Lembrete", icon: <Bell size={16} />, onSelect: () => startCreate("reminder") }
          ]}
        />
      )}
    </header>
  );
}
